import { describeError, useApi } from '@classgo/app-kit';
import type { RoleTemplateView } from '@classgo/shared';
import { Badge, Button, Card, EmptyState, Input, Modal, Table, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { useT } from '../../i18n';
import { useTenant } from '../../tenant/tenant-provider';
import { usersApi, usersQueries } from '../../users/api';
import { PermissionPicker } from './permission-picker';

type Draft = {
  id: string | null;
  name: string;
  description: string;
  keys: Set<string>;
  users: number;
};

const fresh = (): Draft => ({ id: null, name: '', description: '', keys: new Set(), users: 0 });

/**
 * The school's permission templates (#130): what each grants, who holds it, and the editor that
 * composes one from the catalog in plain Spanish. ClassGO's defaults are copied, never destroyed.
 */
export const RolesScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queries = useQueryClient();
  const { session } = useTenant();
  const can = (key: string) => session.permissions.all || session.permissions.keys.includes(key);
  const roles = useQuery(usersQueries.roles(api));
  const catalog = useQuery(usersQueries.permissions(api));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<RoleTemplateView | null>(null);
  const refresh = () => queries.invalidateQueries({ queryKey: ['users'] });

  const save = useMutation({
    mutationFn: (input: Draft) => {
      const body = {
        name: input.name.trim(),
        description: input.description.trim() || null,
        permissionKeys: [...input.keys],
      };
      return input.id ? usersApi(api).updateRole(input.id, body) : usersApi(api).createRole(body);
    },
    onSuccess: (_, input) => {
      toast.show({ tone: 'success', title: t(input.id ? 'roles.saved' : 'roles.created') });
      setDraft(null);
      void refresh();
    },
    onError: (error) =>
      toast.show({ tone: 'error', title: t('roles.title'), description: describeError(t, error) }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => usersApi(api).deleteRole(id),
    onSuccess: () => {
      toast.show({ tone: 'success', title: t('roles.deleted') });
      setDeleting(null);
      void refresh();
    },
    onError: (error) =>
      toast.show({ tone: 'error', title: t('roles.delete'), description: describeError(t, error) }),
  });

  const edit = (role: RoleTemplateView) =>
    setDraft({
      id: role.id,
      name: role.name,
      description: role.description ?? '',
      keys: new Set(role.permissionKeys),
      users: role.usersCount,
    });
  const copy = (role: RoleTemplateView) =>
    setDraft({
      id: null,
      name: t('roles.copy.name', { name: role.name }),
      description: role.description ?? '',
      keys: new Set(role.permissionKeys),
      users: 0,
    });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (draft && draft.name.trim()) save.mutate(draft);
  };

  return (
    <>
      <Card
        title={t('roles.title')}
        flush
        actions={
          can('roles:write') ? (
            <Button onClick={() => setDraft(fresh())}>{t('roles.new')}</Button>
          ) : undefined
        }
      >
        <p className="px-4 pt-4 text-sm text-gray-600 md:px-6">{t('roles.intro')}</p>
        <div className="mt-3">
          <Table<RoleTemplateView>
            caption={t('roles.caption')}
            loading={roles.isPending}
            rows={roles.data ?? []}
            rowKey={(role) => role.id}
            {...(can('roles:write') ? { onRowClick: edit } : {})}
            empty={
              <EmptyState title={t('roles.empty.title')} description={t('roles.empty.body')} />
            }
            columns={[
              {
                key: 'name',
                header: t('roles.column.name'),
                cell: (role) => (
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{role.name}</span>
                      {role.isSystemDefault ? (
                        <Badge tone="brand">{t('roles.default')}</Badge>
                      ) : null}
                    </div>
                    {role.description ? (
                      <p className="text-xs text-gray-500">{role.description}</p>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'permissions',
                header: t('roles.column.permissions'),
                cell: (role) => t('roles.permissions.count', { count: role.permissionKeys.length }),
              },
              {
                key: 'users',
                header: t('roles.column.users'),
                cell: (role) => t('roles.users.count', { count: role.usersCount }),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                cell: (role) =>
                  can('roles:write') ? (
                    <div
                      className="flex flex-wrap justify-end gap-2"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Button size="sm" variant="ghost" onClick={() => edit(role)}>
                        {t('roles.edit')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => copy(role)}>
                        {t('roles.copy')}
                      </Button>
                      {!role.isSystemDefault && role.usersCount === 0 ? (
                        <Button size="sm" variant="ghost" onClick={() => setDeleting(role)}>
                          {t('roles.delete')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null,
              },
            ]}
          />
        </div>
      </Card>

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? t('roles.edit') : t('roles.new')}
        size="lg"
        actions={
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="role-form" loading={save.isPending}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft ? (
          <form id="role-form" onSubmit={submit} className="space-y-4">
            <Input
              label={t('roles.field.name')}
              required
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
            <Input
              label={t('roles.field.description')}
              help={t('roles.field.description.help')}
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
            {draft.users > 0 ? (
              <p className="rounded-control bg-warning-100 px-3 py-2 text-sm text-warning-700">
                {t('roles.inUse.warning', { count: draft.users })}
              </p>
            ) : null}
            <div>
              <p className="mb-2 text-sm font-medium text-gray-900">
                {t('roles.field.permissions')} ·{' '}
                {t('roles.permissions.count', { count: draft.keys.size })}
              </p>
              <PermissionPicker
                catalog={catalog.data ?? []}
                selected={draft.keys}
                onToggle={(key, checked) => {
                  const keys = new Set(draft.keys);
                  if (checked) keys.add(key);
                  else keys.delete(key);
                  setDraft({ ...draft, keys });
                }}
              />
            </div>
          </form>
        ) : null}
      </Modal>

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={t('roles.delete')}
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              {t('roles.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-700">
          {t('roles.delete.body', { name: deleting?.name ?? '' })}
        </p>
      </Modal>
    </>
  );
};
